/**
 * InquiryCard component - displays a single inquiry with actions
 */

import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { User, Phone, Mail, Calendar, MoreVertical, MessageSquare, Car } from 'lucide-react';
import type { InquiryWithAssignment, LeadInquiryStatus } from '../../services/inquiriesApi';

interface InquiryCardProps {
  inquiry: InquiryWithAssignment;
  onStatusChange: (id: string, status: LeadInquiryStatus) => void;
  onConvert: (inquiry: InquiryWithAssignment) => void;
  onDelete: (id: string) => void;
}

const STATUS_LABELS: Record<LeadInquiryStatus, string> = {
  new: 'Nueva',
  contacted: 'Contactado',
  converted: 'Convertida',
  dismissed: 'Descartada',
};

const STATUS_COLORS: Record<LeadInquiryStatus, string> = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  converted: 'bg-green-100 text-green-800',
  dismissed: 'bg-gray-100 text-gray-600',
};

function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function InquiryCard({
  inquiry,
  onStatusChange,
  onConvert,
  onDelete,
}: InquiryCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const vehicleInfo = inquiry.vehicleInfo;

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Main info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-foreground truncate">
                {inquiry.name}
              </h3>
              <Badge className={STATUS_COLORS[inquiry.status]}>
                {STATUS_LABELS[inquiry.status]}
              </Badge>
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" />
                {inquiry.email}
              </span>
              {inquiry.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" />
                  {inquiry.phone}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {formatDate(inquiry.createdAt)}
              </span>
            </div>

            {/* Service interest */}
            {inquiry.serviceInterest && (
              <div className="mt-2">
                <span className="text-xs font-medium text-muted-foreground">
                  Servicio: {inquiry.serviceInterest}
                </span>
              </div>
            )}

            {/* Vehicle info if present */}
            {vehicleInfo && (
              <div className="mt-2 flex items-center gap-1 text-sm text-muted-foreground">
                <Car className="h-3.5 w-3.5" />
                {vehicleInfo.make} {vehicleInfo.model}
                {vehicleInfo.year && ` (${vehicleInfo.year})`}
                {vehicleInfo.registration && ` - ${vehicleInfo.registration}`}
              </div>
            )}

            {/* Message preview/expanded */}
            <div className="mt-3">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                {isExpanded ? 'Ocultar mensaje' : 'Ver mensaje'}
              </button>
              {isExpanded && (
                <p className="mt-2 text-sm text-foreground bg-muted/50 p-3 rounded-md">
                  {inquiry.message}
                </p>
              )}
            </div>

            {/* Assigned to */}
            {inquiry.assignedStaffName && (
              <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                Asignado a: {inquiry.assignedStaffName}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {inquiry.status === 'new' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onStatusChange(inquiry.id, 'contacted')}
              >
                Marcar contactado
              </Button>
            )}
            {(inquiry.status === 'new' || inquiry.status === 'contacted') && (
              <Button
                variant="default"
                size="sm"
                onClick={() => onConvert(inquiry)}
              >
                Convertir
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {inquiry.status !== 'contacted' && (
                  <DropdownMenuItem
                    onClick={() => onStatusChange(inquiry.id, 'contacted')}
                  >
                    Marcar como contactado
                  </DropdownMenuItem>
                )}
                {inquiry.status !== 'dismissed' && (
                  <DropdownMenuItem
                    onClick={() => onStatusChange(inquiry.id, 'dismissed')}
                  >
                    Descartar
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => onDelete(inquiry.id)}
                >
                  Eliminar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
